/*
 * Which incoming orders still have to be announced to the person at the counter.
 *
 * Extracted from the till's poll because it is the logic that decides whether a
 * paid customer standing at the counter is mentioned at all, and it was wrong in
 * production with nothing testing it.
 */

type Announceable = { id: string; taken?: boolean; takenBy?: string };

/*
 * What counts as "already dealt with" on the first poll of a fresh process.
 *
 * A claim is the record of a person having acted, and the agent holds it, so it
 * survives a restart of this app. It is therefore the only honest thing to seed
 * from.
 *
 * `taken` and not `takenBy`: the latter is `seen_by`, a user id, and a till
 * whose session carries an empty one claims orders with `seen_by = ''` — so on
 * that device every claimed order reads as untouched. `takenBy` is still
 * honoured for an agent too old to send `taken`; where neither is set the order
 * is announced, which is the direction that costs a duplicate alert rather than
 * a silent customer.
 *
 * The first poll used to be swallowed whole, on the reasoning that whatever is
 * already waiting when a cashier signs in is not news. That reasoning fails in
 * the case that matters: the till restarts — an update, a power cut, a crash —
 * and every order still waiting for «ارسال به آشپزخانه» goes permanently
 * silent. It happened, with a customer at the counter and three untaken orders
 * behind a badge reading ۳ that nobody had been told about.
 */
export function seedAnnounced(list: Announceable[]): Set<string> {
  return new Set(list.filter(isClaimed).map((a) => a.id));
}

/*
 * Whether a person has claimed this order.
 *
 * Exported because the rule above was written here, tested here, and then
 * re-derived by eye in the arrivals screen as `Boolean(takenBy)` — which is the
 * one reading this comment exists to rule out. Nineteen claimed orders rendered
 * as untouched on a till with no signed-in cashier, because `seen_by` was ''.
 * One predicate, one place, so the screen and the announcement cannot disagree
 * about whether an order has been dealt with.
 */
export function isClaimed(a: Announceable): boolean {
  return a.taken ?? Boolean(a.takenBy);
}

/*
 * The ones to announce, and the set to remember them by.
 *
 * `seen` is only ever added to: a dropped poll or a reconnect re-sends orders a
 * cashier has already dismissed, and re-popping those teaches them to dismiss
 * without reading, which is how the announcement stops working at all.
 */
export function unannouncedFrom<T extends Announceable>(
  list: T[],
  seen: Set<string> | null,
): { seen: Set<string>; fresh: T[] } {
  const known = seen ?? seedAnnounced(list);
  const fresh = list.filter((a) => !known.has(a.id));
  for (const a of fresh) known.add(a.id);
  return { seen: known, fresh };
}
